import React, {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from './auth';
import { STORAGE_KEYS } from '../constants/Config';
import { getNotificationConfig } from '../constants/notificationTypes';
import {
  invalidarPainelDeNotificacoes,
  resolverHandlerDeNotificacao,
} from '../constants/notificationHandlers';
import { IWebSocketMessage } from '../hooks/useNotificacoes';
import { api } from '../services/api';

interface NotificationContextData {
  unreadCount: number;
  isConnected: boolean;
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
}

interface AuxProps {
  children: ReactElement | ReactElement[];
}

const NotificationContext = createContext<NotificationContextData>({} as NotificationContextData);

const MAX_RECONNECT_ATTEMPTS = 5;

// Sessao 2026-05-14 (review Matias PR #345 #11): token NAO vai mais na URL.
// Vai no primeiro frame apos `onopen` (formato `{type:'auth', token}`) — sem
// isso, o JWT aparecia em logs de proxy reverso, access logs de nginx e no
// historico do navegador. Backend (notificacoes.py) aceita a partir desta
// versao via path `/ws` sem param. Mantemos `wss` em prod via REACT_APP_API_URL.
function getWsUrl(): string {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const wsBase = apiUrl.replace(/^http/, 'ws').replace(/\/$/, '');
  return `${wsBase}/api/v1/notificacoes/ws`;
}

function getAccessToken(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.access_token || null;
  } catch {
    return null;
  }
}

export const NotificationProvider: React.FC<AuxProps> = ({ children }) => {
  const { signed } = useAuth();
  const queryClient = useQueryClient();

  const [isConnected, setIsConnected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Contagem de não-lidas via useQuery — invalida automaticamente
  const { data: unreadData } = useQuery({
    queryKey: ['notificacoes-unread-count'],
    queryFn: async () => {
      const res = await api.get('/api/v1/notificacoes/nao-lidas/count/');
      return res.data?.data?.total ?? 0;
    },
    enabled: signed,
    refetchInterval: 60000, // Refetch a cada 60s como fallback
  });
  const unreadCount = unreadData ?? 0;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message: IWebSocketMessage = JSON.parse(event.data);
        const config = getNotificationConfig(message.type);

        // ⚠️ `id` estável por tipo+referência: sem ele cada mensagem abria um
        // toast NOVO, e o WebSocket reentrega ao reconectar — no canto direito
        // iam se acumulando avisos que ficavam por cima do sino de
        // notificações, atrapalhando o clique. Com id, o sonner substitui o
        // anterior em vez de empilhar. `dismissible` para o operador poder
        // tirar da frente na hora.
        const refToast = message.data?.contrato_id ?? message.data?.card_id ?? '';
        toast(config.toastLabel, {
          id: `notif-${message.type}-${refToast}`,
          description: message.data.mensagem,
          duration: 6000,
          dismissible: true,
        });

        // Efeito por domínio: o dono do tipo declara o que invalidar, no
        // `notificacoes.ts` do próprio módulo. Este provider não conhece mais
        // nenhuma queryKey de domínio — ver `constants/notificationHandlers.ts`.
        //
        // ⚠️ A guarda de `data` fica AQUI, e não repetida em cada handler: o
        // tipo garante os campos, mas o socket entrega JSON de fora e uma
        // mensagem sem `data` faria o handler estourar. Estourando aqui dentro,
        // o `catch` engoliria também a invalidação do painel logo abaixo — o
        // sino pararia de atualizar por causa de uma mensagem malformada de um
        // domínio qualquer.
        if (message.data) {
          resolverHandlerDeNotificacao(message.type)?.({ message, queryClient });
        }
        // O painel e o contador mudam em TODA notificação, tenha ela dono ou
        // não. Antes cada um dos 7 ramos repetia estas duas invalidações.
        invalidarPainelDeNotificacoes(queryClient);
      } catch {
        // Ignora mensagens malformadas
      }
    },
    [queryClient],
  );

  const connect = useCallback(() => {
    if (!isMountedRef.current) return;

    const token = getAccessToken();
    if (!token) return;

    // Fecha conexão existente
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const ws = new WebSocket(getWsUrl());

    ws.onopen = () => {
      if (!isMountedRef.current) {
        ws.close();
        return;
      }
      // Sessao 2026-05-14 (review Matias PR #345 #11): envia auth no primeiro
      // frame — token nao trafega mais em URL. Se o send falhar (raro,
      // socket caido entre handshake e este callback), fecha e deixa o
      // backoff reconectar.
      try {
        ws.send(JSON.stringify({ type: 'auth', token }));
      } catch {
        ws.close();
        return;
      }
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
      // Sessao 2026-05-14 (review Matias PR #345 #12): ao (re)conectar,
      // invalida a lista e o contador de nao-lidas. Cobre o gap em que o WS
      // ficou offline e notificacoes foram so persistidas no DB sem chegar
      // via push — o refetch traz tudo de uma vez.
      invalidarPainelDeNotificacoes(queryClient);
    };

    ws.onmessage = handleMessage;

    ws.onerror = () => {
      // onclose será chamado em seguida
    };

    ws.onclose = event => {
      if (!isMountedRef.current) return;
      setIsConnected(false);

      // Código 4001 = token inválido, não tenta reconectar
      if (event.code === 4001) return;

      // Exponential backoff
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = 1000 * 2 ** (reconnectAttemptsRef.current + 1);
        reconnectAttemptsRef.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    wsRef.current = ws;
  }, [handleMessage, queryClient]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = 0;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Conectar/desconectar baseado no estado de auth
  useEffect(() => {
    if (signed) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [signed, connect, disconnect]);

  // Sessao 2026-05-14 (review Matias PR #345 #12): reconecta ao voltar
  // online. Sem isso, se as 5 tentativas exponenciais estouraram durante a
  // queda de rede (totalizam ~62s), o contador permanecia em MAX e o WS
  // ficava offline pra sempre ate o proximo refresh — notificacoes geradas
  // no servidor enquanto isso vinham so via fallback de polling de 60s.
  useEffect(() => {
    if (!signed) return undefined;
    const handleOnline = () => {
      if (!isMountedRef.current) return;
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      connect();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [signed, connect]);

  // Cleanup no unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <NotificationContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        unreadCount,
        isConnected,
        isOpen,
        openPanel,
        closePanel,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within a NotificationProvider.');
  }
  return context;
}
